/**
 * Keyword Research Page — /seo/keyword-research
 *
 * Full-page promotion of the ResearchSessionsDrawer content.
 * Manages topical map creation, AI seed suggestions, and cluster browsing.
 * "Create Articles" sends selected clusters to the Content Planner.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
	Database,
	Search,
	RefreshCw,
	Sparkles,
	ChevronDown,
	ChevronRight,
	ExternalLink,
	BookOpen,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTenantStore } from '@/stores/useTenantStore';
import { useBrandBible } from '@/hooks/useBrandBible';
import {
	useTopicalMaps,
	useSeedKeywordAnalysis,
} from '@/hooks/useContentPlannerAPI';
import { contentPlannerService } from '@/services/contentPlannerService';
import { ServiceClusterPanel } from '@/components/keyword-research/ServiceClusterPanel';
import { SeedKeywordSuggestions } from '@/components/content-planner/SeedKeywordAnalyzer';
import { ResearchActivityLog } from '@/components/content-planner/ResearchActivityLog';
import type { SeedKeywordAnalysis, ProcessingLogEntry } from '@/types/contentPlanner';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
	pending: 'Pending',
	fetching_keywords: 'Fetching Keywords',
	classifying: 'Classifying Intent',
	clustering: 'Clustering',
	organizing: 'Organizing',
};

const SUGGEST_MESSAGES = [
	'Analyzing your brand profile...',
	'Generating keyword suggestions with AI...',
	'Enriching keywords with search volume data...',
	'Computing opportunity scores and rankings...',
	'Finalizing analysis — almost done...',
];

// ---------------------------------------------------------------------------
// No-tenant guard
// ---------------------------------------------------------------------------

function NoTenantState() {
	return (
		<div className="flex items-center justify-center h-64 text-muted-foreground">
			No workspace selected.
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function KeywordResearchPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { currentTenant } = useTenantStore();
	const tenantId = currentTenant ?? '';

	// Redirect legacy ?tab=ads bookmarks to Ads Tools. The "Ad campaigns"
	// tab used to live on this page; promoted because SEO and Google Ads
	// jobs are different intents that deserve separate IA.
	useEffect(() => {
		if (searchParams.get('tab') === 'ads') {
			navigate('/ads-tools/keyword-research?platform=google-ads', { replace: true });
		}
	}, [searchParams, navigate]);

	// Brand Bible for AI suggestions
	const { data: brandBible } = useBrandBible(tenantId);

	// Topical maps
	const { data: mapsData, refetch: refetchMaps } = useTopicalMaps(tenantId);
	const allMaps = mapsData?.maps ?? [];

	const inProgressMaps = useMemo(
		() => allMaps.filter((m) => !['completed', 'failed'].includes(m.status)),
		[allMaps],
	);

	// Research state
	const [seedKeyword, setSeedKeyword] = useState(
		searchParams.get('keyword') ?? '',
	);
	const [isResearching, setIsResearching] = useState(false);
	const [researchProgress, setResearchProgress] = useState<{
		status: string;
		progress: number;
		stage?: string;
	} | null>(null);
	const [processingLogs, setProcessingLogs] = useState<ProcessingLogEntry[]>([]);
	const [processingStartedAt, setProcessingStartedAt] = useState<string | null>(null);
	const [expandedInProgressMapId, setExpandedInProgressMapId] = useState<string | null>(null);

	// AI suggestions
	const [suggestions, setSuggestions] = useState<SeedKeywordAnalysis[]>([]);
	const [suggestionsOpen, setSuggestionsOpen] = useState(false);
	const analyzeMutation = useSeedKeywordAnalysis();

	// Rotating progress messages during AI suggest
	const [suggestMsgIndex, setSuggestMsgIndex] = useState(0);
	useEffect(() => {
		if (!analyzeMutation.isPending) { setSuggestMsgIndex(0); return; }
		const interval = setInterval(() => {
			setSuggestMsgIndex((prev) => Math.min(prev + 1, SUGGEST_MESSAGES.length - 1));
		}, 8000);
		return () => clearInterval(interval);
	}, [analyzeMutation.isPending]);

	// ---- Handlers ----

	const handleStartResearch = useCallback(() => {
		if (!seedKeyword.trim() || !tenantId) return;
		setIsResearching(true);
		setResearchProgress({ status: 'starting', progress: 0, stage: 'Initializing...' });
		setProcessingLogs([]);
		setProcessingStartedAt(null);

		contentPlannerService.createMapWithStreaming(
			{ seed_keyword: seedKeyword.trim(), region: 'US', language: 'en' },
			{
				onCreated: (mapId) => {
					setExpandedInProgressMapId(mapId);
					refetchMaps();
				},
				onProgress: (status, progress, stage) => {
					setResearchProgress({ status, progress, stage });
					refetchMaps();
				},
				onActivityLog: (entries, _total, startedAt) => {
					setProcessingLogs((prev) => [...prev, ...entries]);
					if (startedAt) setProcessingStartedAt(startedAt);
				},
				onComplete: (_mapId, _status, fromCache) => {
					setIsResearching(false);
					setResearchProgress(null);
					setProcessingLogs([]);
					setProcessingStartedAt(null);
					setExpandedInProgressMapId(null);
					setSeedKeyword('');
					refetchMaps();
					toast.success(
						fromCache
							? 'Found existing research for this keyword!'
							: 'Keyword research complete!',
					);
				},
				onError: (msg) => {
					setIsResearching(false);
					setResearchProgress(null);
					toast.error(`Research failed: ${msg}`);
				},
			},
		);
	}, [seedKeyword, tenantId, refetchMaps]);

	const handleAISuggest = useCallback(async () => {
		if (!brandBible) return;
		const hasContent =
			(brandBible.services_offered && brandBible.services_offered.length > 0) ||
			(brandBible.customer_pain_points && brandBible.customer_pain_points.trim().length > 0) ||
			(brandBible.marketing_focus && brandBible.marketing_focus.trim().length > 0) ||
			(brandBible.ideal_client && brandBible.ideal_client.trim().length > 0);

		if (!hasContent) {
			toast('Your Brand Bible needs more details. Add services, pain points, or marketing focus to get AI suggestions.', { duration: 5000 });
			return;
		}
		try {
			const result = await analyzeMutation.mutateAsync({
				services_offered: brandBible.services_offered || [],
				customer_pain_points: brandBible.customer_pain_points || '',
				marketing_focus: brandBible.marketing_focus || '',
				ideal_client: brandBible.ideal_client || '',
				business_name: brandBible.business_name || '',
			});
			setSuggestions(result.suggestions);
			if (result.suggestions.length > 0) setSuggestionsOpen(true);
			else toast('No suggestions generated. Try updating your Brand Bible with more details.');
		} catch {
			toast.error('Failed to analyze keywords. Please try again.');
		}
	}, [brandBible, analyzeMutation]);

	if (!currentTenant) return <NoTenantState />;

	return (
		<div className="w-full space-y-6 pb-12">
			{/* Page header */}
			<div className="flex items-start justify-between">
				<div className="flex items-start gap-4">
					<div className="w-12 h-12 rounded-xl bg-teal-light flex items-center justify-center shrink-0">
						<Database className="w-6 h-6 text-teal" />
					</div>
					<div>
						<h1 className="text-2xl font-bold tracking-tight">Keyword Research</h1>
						<p className="text-muted-foreground mt-0.5">
							Pick your goal — find blog topics for organic traffic, or build
							ad-group clusters for paid campaigns.
						</p>
					</div>
				</div>
				<Button
					variant="outline"
					size="sm"
					onClick={() => navigate('/content/planner')}
					className="h-8 text-xs gap-1.5 shrink-0"
				>
					<BookOpen className="w-3.5 h-3.5" />
					Content Planner
					<ExternalLink className="w-3 h-3" />
				</Button>
			</div>

			<div className="space-y-6">
			{/* Research input card */}
			<Card className="border shadow-sm">
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-base">
							<Search className="w-4 h-4 text-teal" />
							New Research
						</CardTitle>
					<p className="text-xs text-muted-foreground">
						Enter a seed keyword to fetch volume, difficulty, and cluster topics.
					</p>
				</CardHeader>
				<CardContent className="space-y-3">
					<form
						onSubmit={(e) => { e.preventDefault(); handleStartResearch(); }}
						className="flex items-center gap-2"
					>
						<Input
							value={seedKeyword}
							onChange={(e) => setSeedKeyword(e.target.value)}
							placeholder={brandBible?.services_offered?.[0] ? `e.g. ${brandBible.services_offered[0]}...` : "e.g. your main service..."}
							className="h-10 text-sm"
							disabled={isResearching}
						/>
						{brandBible && (
							<Button
								type="button"
								variant="outline"
								className={`${analyzeMutation.isPending ? "" : "animate-ai-suggest"} h-10 shrink-0 text-teal border-teal/30 hover:bg-teal-light hover:border-teal/50`}
								onClick={handleAISuggest}
								disabled={analyzeMutation.isPending}
							>
								{analyzeMutation.isPending ? (
									<RefreshCw className="h-4 w-4 animate-spin" />
								) : (
									<>
										<Sparkles className="h-4 w-4 mr-1.5" />
										{suggestions.length > 0 ? 'Refresh Suggestions' : 'AI Suggest'}
									</>
								)}
							</Button>
						)}
						<Button
							type="submit"
							className="h-10 shrink-0 bg-teal hover:bg-teal-hover text-white"
							disabled={!seedKeyword.trim() || isResearching}
						>
							{isResearching ? (
								<RefreshCw className="h-4 w-4 animate-spin" />
							) : (
								<>
									<Search className="h-4 w-4 mr-1.5" />
									Research
								</>
							)}
						</Button>
					</form>

					{!brandBible && (
						<p className="text-xs text-muted-foreground">
							Set up your{' '}
							<button
								onClick={() => navigate('/brand-bible')}
								className="text-teal underline-offset-2 hover:underline"
							>
								Brand Bible
							</button>{' '}
							to unlock AI-powered keyword suggestions.
						</p>
					)}

					{/* AI suggest loading message */}
					{analyzeMutation.isPending && (
						<div className="flex items-center gap-2 text-xs text-teal-dark">
							<RefreshCw className="h-3 w-3 animate-spin shrink-0" />
							<span className="animate-pulse">{SUGGEST_MESSAGES[suggestMsgIndex]}</span>
						</div>
					)}

					{/* Inline progress bar — before in-progress card appears */}
					{isResearching && researchProgress && inProgressMaps.length === 0 && (
						<div className="space-y-1">
							<div className="flex items-center justify-between text-xs text-muted-foreground">
								<span className="truncate">{researchProgress.stage || 'Starting...'}</span>
								<span className="shrink-0 font-medium text-teal-dark">{researchProgress.progress}%</span>
							</div>
							<div className="h-1.5 bg-muted rounded-full overflow-hidden">
								<div
									className="h-full bg-teal rounded-full transition-all duration-500 ease-out"
									style={{ width: `${researchProgress.progress}%` }}
								/>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			{/* AI Suggestions */}
			{suggestions.length > 0 && (
				<Card className="border shadow-sm">
					<CardContent className="pt-4">
						<SeedKeywordSuggestions
							suggestions={suggestions}
							onSelectKeyword={(kw) => setSeedKeyword(kw)}
							isOpen={suggestionsOpen}
							onOpenChange={setSuggestionsOpen}
						/>
					</CardContent>
				</Card>
			)}

			{/* In-progress sessions */}
			{inProgressMaps.length > 0 && (
				<div className="space-y-3">
					<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide px-0.5">
						In Progress
					</h2>
					{inProgressMaps.map((map) => {
						const isExpanded = expandedInProgressMapId === map.map_id;
						const progress = researchProgress?.progress ?? map.progress_percentage;
						const stage = researchProgress?.stage || STATUS_LABELS[map.status] || map.status;

						return (
							<Card key={map.map_id} className="border border-teal/20 shadow-sm overflow-hidden">
								<button
									type="button"
									onClick={() =>
										setExpandedInProgressMapId((prev) =>
											prev === map.map_id ? null : map.map_id,
										)
									}
									className="w-full bg-teal-light hover:bg-teal/10 transition-colors px-5 py-4 text-left"
								>
									<div className="flex items-center gap-3">
										<RefreshCw className="h-4 w-4 text-teal animate-spin shrink-0" />
										<div className="flex-1 min-w-0">
											<span className="text-sm font-semibold truncate block">{map.seed_keyword}</span>
											<span className="text-xs text-muted-foreground">{stage}</span>
										</div>
										<span className="text-sm font-semibold text-teal-dark shrink-0">{progress}%</span>
										{isExpanded ? (
											<ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
										) : (
											<ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
										)}
									</div>
									<div className="mt-3 h-1.5 bg-teal/10 rounded-full overflow-hidden">
										<div
											className="h-full bg-teal rounded-full transition-all duration-500 ease-out"
											style={{ width: `${progress}%` }}
										/>
									</div>
								</button>

								{isExpanded && (() => {
									const liveEntries = processingLogs.length > 0 ? processingLogs : null;
									const apiEntries = map.processing_log?.filter(Boolean) ?? [];
									const entries = liveEntries ?? apiEntries;
									const started = processingStartedAt ?? map.processing_started_at ?? null;
									return (
										<div className="border-t border-teal/10 bg-card px-5 py-3">
											<ResearchActivityLog
												entries={entries}
												startedAt={started}
												isProcessing={isResearching}
											/>
										</div>
									);
								})()}
							</Card>
						);
					})}
				</div>
			)}

			{/* Service clusters — the persistent, service-shaped pipeline. This is
			    the organising unit: keywords belong to the service that sells them,
			    not to whichever seed phrase someone happened to research. It renders
			    its own empty state, so no separate empty state is needed here. */}
			<ServiceClusterPanel tenantId={tenantId} />
			</div>
		</div>
	);
}
